import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Button,
  Container,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Link,
} from '@mui/material';
import { textStyles } from '../styles/textstyle';
import Pagination from '../components/main/Pagination';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { getOrders, Order, OrdersParams } from '../api/orders';

const MyOrders: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [limit] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [currentPage]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params: OrdersParams = {
        page: currentPage,
        limit,
      };
      
      const response = await getOrders(params);
      // The API returns an array directly, not an object with orders property
      setOrders(response);
      setTotalPages(1); // Since we're getting all orders in one response
      setTotalOrders(response.length);
    } catch (err) {
      setError('Failed to fetch orders');
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
    setOrderDetailsOpen(true);
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsOpen(false);
    setSelectedOrder(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };



  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
      case 'processing':
        return '#ffc107';
      case 'shipped':
      case 'delivered':
        return '#28a745';
      case 'cancelled':
      case 'failed':
        return '#dc3545';
      case 'paid':
        return '#4CAF50';
      default:
        return '#6c757d';
    }
  };

  // Helper function to determine if order should show PAID status for Stripe payments
  const getDisplayStatus = (order: Order) => {
    // Check if payment method is Stripe
    const isStripePayment = order.paymentMethod?.name?.toLowerCase().includes('stripe') || 
                           order.paymentMethod?.provider?.toLowerCase().includes('stripe');
    
    if (isStripePayment) {
      // For Stripe payments, show PAID if order status indicates payment success
      // The backend should update the order status to 'paid' when Stripe payment succeeds
      if (order.order.status?.toLowerCase() === 'paid') {
        return 'PAID';
      }
    }
    
    return order.order.status.toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    // Convert currency symbols to ISO currency codes
    let currencyCode = currency;
    if (currency === 'HK$') {
      currencyCode = 'HKD';
    } else if (currency === '$') {
      currencyCode = 'USD';
    } else if (currency === '€') {
      currencyCode = 'EUR';
    } else if (currency === '£') {
      currencyCode = 'GBP';
    } else if (currency === '¥') {
      currencyCode = 'JPY';
    }
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      }).format(amount);
    } catch {
      // Fallback to simple formatting if currency code is invalid
      return `${currency}${amount.toFixed(2)}`;
    }
  };

  if (loading && orders.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && orders.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" variant="h6" gutterBottom>
          {error}
        </Typography>
        <Button variant="contained" onClick={fetchOrders} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ 
        bgcolor: 'white', 
        borderBottom: '1px solid #e0e0e0',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            p: 2,
            gap: 2
          }}>
            <IconButton onClick={handleBack} sx={{ color: '#B3907A' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#B3907A', 
                fontWeight: 600
              }}
            >
              {t('orders.myOrders')}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header Stats */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={700} color="#212529">
            {t('orders.totalOrders')}: {totalOrders}
          </Typography>
        </Box>

        {/* Orders List */}
        {!orders || orders.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ShoppingBagIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('orders.noOrders')}
            </Typography>
            <Typography color="text.secondary">
              {t('orders.noOrdersDescription')}
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {orders.map((order) => (
              <Card 
                key={order.order.id}
                sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    transition: 'box-shadow 0.3s ease'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Order Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={700} color="#212529" gutterBottom>
                        {t('orders.orderNumber')}: {order.order.orderNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('orders.orderedOn')}: {formatDate(order.order.createdAt)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ textAlign: 'right' }}>
                      <Chip
                        label={getDisplayStatus(order)}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(getDisplayStatus(order).toLowerCase()),
                          color: 'white',
                          fontWeight: 600,
                          mb: 1,
                        }}
                      />
                      <Typography variant="h6" fontWeight={700} color="#B3907A">
                        {formatCurrency(order.order.finalTotal, order.currency)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Order Items Preview */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} mb={1}>
                      {t('orders.items')} ({order.items.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {order.items.slice(0, 3).map((item) => (
                        <Box key={item.productId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img
                            src={item.imageUrl}
                            alt={item.productName}
                            style={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 4,
                            }}
                            onError={(e) => {
                              e.currentTarget.src = '/images/img_placeholder.svg';
                            }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" noWrap>
                              {item.productName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Qty: {item.quantity}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      {order.items.length > 3 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            +{order.items.length - 3} {t('orders.moreItems')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Shipping Address */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} mb={1}>
                      {t('orders.shippingAddress')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {order.shippingAddress.firstName} {order.shippingAddress.lastName}
                      <br />
                      {order.shippingAddress.address}
                      <br />
                      {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.country}
                    </Typography>
                  </Box>

                                      {/* Action Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleViewDetails(order)}
                        sx={{
                          borderColor: '#B3907A',
                          color: '#B3907A',
                          '&:hover': {
                            borderColor: '#B3907A',
                            bgcolor: 'rgba(179, 144, 122, 0.04)',
                          },
                        }}
                      >
                        {t('orders.viewDetails')}
                      </Button>
                    </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </Box>
        )}
      </Container>

      {/* Order Details Dialog */}
      {selectedOrder && (
        <Dialog
          open={orderDetailsOpen}
          onClose={handleCloseOrderDetails}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '90vh',
            },
          }}
        >
          <DialogTitle sx={{ 
            borderBottom: '1px solid #e0e0e0',
            pb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography sx={textStyles.h6}>
              {t('orders.orderDetails')} - {selectedOrder.order.orderNumber}
            </Typography>
            <IconButton onClick={handleCloseOrderDetails} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            {/* Shipping Information */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={textStyles.h6} mb={2}>
                {t('orders.shippingInfo')}
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  backgroundColor: '#F8F9FA',
                  border: '1px solid #E9ECEF',
                  borderRadius: '4px',
                }}
              >
                {selectedOrder.order.shippingCarrier && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#212529' }}>
                      {t('orders.shippingCarrier')}:
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#B3907A' }}>
                      {selectedOrder.order.shippingCarrier}
                    </Typography>
                  </Box>
                )}
                
                {selectedOrder.order.trackingNumber && (
                  <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#212529' }}>
                        {t('orders.trackingCode')}:
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#B3907A' }}>
                        {selectedOrder.order.trackingNumber}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" justifyContent="center">
                      <Link
                        href={`https://htm.sf-express.com/hk/en/dynamic_function/waybill/?waybillNo=${selectedOrder.order.trackingNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: '#1976D2',
                          textDecoration: 'underline',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#1565C0',
                            textDecoration: 'none',
                          },
                        }}
                      >
                        {t('orders.clickToTrack')}
                      </Link>
                    </Box>
                  </>
                )}
                
                {!selectedOrder.order.trackingNumber && !selectedOrder.order.shippingCarrier && (
                  <Typography sx={{ fontSize: '14px', color: '#6B6B6B', textAlign: 'center' }}>
                    Tracking information will be available once your order is shipped.
                  </Typography>
                )}
              </Paper>
            </Box>

            {/* Products Section - Matching CheckoutProductList styling */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={textStyles.h6} mb={2}>
                {t('orders.products')}
              </Typography>
              {selectedOrder.items.map((item) => (
                <Paper
                  key={item.productId}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' }, // Column on mobile, row on desktop
                    alignItems: { xs: 'stretch', md: 'center' },
                    gap: 2,
                    mt: 2,
                    boxShadow: 'none',
                    border: 'none',
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    width: { xs: '100%', md: 'auto' }
                  }}>
                    <img
                      src={item.imageUrl || '/images/img_placeholder.svg'}
                      alt={item.productName}
                      style={{
                        width: 90,
                        height: 90,
                        objectFit: 'cover',
                        borderRadius: 4,
                      }}
                      onError={(e) => {
                        e.currentTarget.src = '/images/img_placeholder.svg';
                      }}
                    />
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    flex={1}
                    minWidth={0}
                    alignItems={{ xs: 'center', md: 'flex-start' }}
                    textAlign={{ xs: 'center', md: 'left' }}
                  >
                    <Typography mb={0.5} sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: 0,
                      verticalAlign: 'middle',
                    }}>
                      {item.productName}
                    </Typography>
                    <Typography fontSize={14} fontWeight={400} color="text.primary" mb={0.5}>
                      QTY: {item.quantity}
                    </Typography>
                  </Box>
                  <Box 
                    display="flex" 
                    flexDirection="column" 
                    alignItems={{ xs: 'center', md: 'flex-end' }} 
                    justifyContent="center" 
                    minWidth={{ xs: 'auto', md: 100 }}
                    width={{ xs: '100%', md: 'auto' }}
                  >
                    {item.totalComparePrice > item.totalPrice && (
                      <>
                        <Typography
                          fontSize={12}
                          fontWeight={400}
                          color="#B3907A"
                          sx={{ textDecoration: 'line-through' }}
                          mb={0.5}
                        >
                          {selectedOrder.currency}{item.totalComparePrice?.toFixed(2)}
                        </Typography>
                        <Typography
                          fontSize={12}
                          fontWeight={400}
                          color="#4CAF50"
                          mb={0.5}
                        >
                          Save {selectedOrder.currency}{(item.totalComparePrice - item.totalPrice).toFixed(2)}
                        </Typography>
                      </>
                    )}
                    <Typography fontSize={16} fontWeight={700} color="#17110D">
                      {selectedOrder.currency}{item.totalPrice?.toFixed(2)}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>

            {/* Order Summary - Matching OrderSummary styling */}
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                backgroundColor: '#F2EFEA',
                border: '1px solid #E1DACA',
                borderRadius: '4px',
              }}
            >
              <Typography sx={textStyles.h6} mb={2}>
                {t('orders.orderSummary')}
              </Typography>
              
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography sx={textStyles.orderSummaryLabel}>
                  {t('orders.totalItemsValue')}
                </Typography>
                <Typography sx={textStyles.orderSummaryLabel}>
                  {selectedOrder.currency}{selectedOrder.calculation.totalItemsValue?.toFixed(2)}
                </Typography>
              </Box>
              
              {/* Discount Details - Matching DiscountDetails styling */}
              {selectedOrder.validatedDiscounts.appliedDiscounts.length > 0 && (
                <Box>
                  {selectedOrder.validatedDiscounts.appliedDiscounts.map((discount) => (
                    <Box
                      key={discount.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                      sx={{ pl: 4 }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography sx={{ color: '#4CAF50', fontSize: '14px', fontWeight: 400 }}>
                          {discount.name}
                        </Typography>
                        <Chip
                          label="AUTO"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '10px',
                            fontWeight: 700,
                            backgroundColor: '#E3F2FD',
                            color: '#1976D2',
                            '& .MuiChip-label': {
                              px: 1,
                            },
                          }}
                        />
                      </Box>
                      <Typography sx={{ color: '#4CAF50', fontSize: '14px', fontWeight: 400 }}>
                        -{selectedOrder.currency}{discount.discountAmount?.toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              
              <Box display="flex" justifyContent="space-between" mb={1} mt={2}>
                <Typography sx={textStyles.orderSummaryUnderlined}>
                  {t('orders.shipping')}
                </Typography>
                <Typography sx={textStyles.orderSummaryValue}>
                  {selectedOrder.currency}{selectedOrder.calculation.shippingCost?.toFixed(2)}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2, color: '#ECECEC' }} />
              
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography sx={textStyles.orderSummaryTotal}>
                  {t('orders.orderTotal')}
                </Typography>
                <Typography sx={textStyles.orderSummaryTotal}>
                  {selectedOrder.currency}{selectedOrder.order.finalTotal?.toFixed(2)}
                </Typography>
              </Box>
            </Paper>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default MyOrders;
